import convertDataWithTrendsToAnalyticsData from 'common/src/modules/analytics/utils/convertDataWithTrendsToAnalyticsData';
import {DashboardWidgetType} from 'common/src/modules/dashboards/dashboards.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import forceTimeZone from 'common/src/modules/timezones/utils/forceTimeZone';
import getTimezoneObjectByAbbreviation from 'common/src/modules/timezones/utils/getTimezoneObjectByAbbreviation';
import {CLStore} from 'common/src/redux/store';
import _map from 'lodash/map';
import _uniq from 'lodash/uniq';
import {DateTime} from 'luxon';
import analyticsApi from '../../../../analytics.api';
import {
  AnalyticsData,
  AnalyticsDataWithTrends,
  AnalyticsEventType,
  DimensionValue,
  MetricValue,
} from '../../../../analytics.types';
import getRemoteDimensionKey from '../../../../utils/getRemoteDimensionKey';
import {copilotAnalyticsTableWidgetInputSchema} from '../../../copilotAnalytics.schemas';
import {CampaignAnalyticsToolName} from '../campaignAnalyticsAgent.enums';

function getCampaignAnalyticsTableDataTool(store: CLStore) {
  function handleNoAnalyticsData() {
    return toolError('Data is unavailable, please try again later');
  }

  async function execute({
    input: {
      dimensions: aiDimensions,
      metrics: aiMetrics,
      startDate,
      endDate,
      filter,
      withTrends = false,
    },
  }: ToolExecuteParams<typeof copilotAnalyticsTableWidgetInputSchema>): Promise<ToolResult> {
    const {
      campaignPage: {
        insightsPage: {measuredPeriod, availableFilters, availableDimensions},
        campaignTree: {campaign},
      },
    } = store.getState();

    const timezoneObject = await getTimezoneObjectByAbbreviation(measuredPeriod.timezone);
    const dateRange = {
      startDate: forceTimeZone(DateTime.fromISO(startDate).toUTC(), 0),
      endDate: forceTimeZone(DateTime.fromISO(endDate).toUTC(), 0).endOf('day'),
    };

    const metrics = aiMetrics as MetricValue[];

    const dimensions = _uniq(
      _map(
        aiDimensions,
        (dimension) =>
          getRemoteDimensionKey<DimensionValue>(availableDimensions, dimension) ??
          DimensionValue.Date,
      ),
    );

    const filterKey = getRemoteDimensionKey<DimensionValue>(availableFilters, filter?.dimension);

    const campaignIdFilter = {
      filterBy: DimensionValue.CampaignID,
      allValues: [String(campaign.id)],
    };

    const allFilters = [
      campaignIdFilter,
      ...(filterKey
        ? [
            {
              filterBy: filterKey,
              allValues: filter?.values ?? [],
            },
          ]
        : []),
    ];

    try {
      const data = await analyticsApi.getDataForDashboard<AnalyticsData | AnalyticsDataWithTrends>({
        dateRange,
        metrics,
        dimensions,
        filters: allFilters,
        selectedTimezone: timezoneObject,
        withTrends,
        eventType: AnalyticsEventType.Adserving,
      });

      const analyticsData = withTrends
        ? convertDataWithTrendsToAnalyticsData(data as AnalyticsDataWithTrends[])
        : data;

      return toolResult(
        JSON.stringify({
          analyticsData,
          withTrends,
          type: DashboardWidgetType.MULTI_TABLE,
        }),
      );
    } catch (error) {
      return handleNoAnalyticsData();
    }
  }

  return tool({
    name: CampaignAnalyticsToolName.CampaignAnalyticsTableData,
    displayName: 'Loading Analytics Data',
    description: `Retrieves analytics data by multiple metrics and dimensions as ${DashboardWidgetType.MULTI_TABLE} widget`,
    execute,
    inputSchema: copilotAnalyticsTableWidgetInputSchema,
  });
}

export default getCampaignAnalyticsTableDataTool;
